// AI generator catalog (Phase 3) — browser client. Fetches ai_safe library entries (curated + public
// user) for prompt injection (metadata only) and for resolving AI-emitted {libraryBlockId, version}
// references back to real blocks. Kept separate from the pure catalog logic (engine/aiCatalog/*) so node
// CI bundles the pure parts without Supabase.
//
// CONSUMPTION is gated by the aiCatalog kill switch (useBlockLibraryGate). A block is only offered to the
// AI when it is ai_safe AND public+published — a disabled/delisted block drops out of this query at once.

import { supabase } from './supabaseClient';
import { curatedEntries } from './customBlockLibraryService';
import { searchCatalog, type CatalogCard } from '../engine/aiCatalog/catalog';
import { resolveLibraryReference } from '../engine/aiCatalog/resolveReference';
import type { Block } from '../types';
import type { LibraryEntry } from '../types/customBlockLibrary';

interface VersionRow { version: number; code: string | null; tsl_ast?: unknown; param_schema: Record<string, unknown> }
interface Row {
  id: string; author_id: string; category: LibraryEntry['category']; block_type: LibraryEntry['blockType'];
  name: string; description: string; tags: string[]; ai_safe: boolean; optimizer_safe: boolean;
  status: LibraryEntry['status']; downloads: number; custom_block_versions: VersionRow[];
}

function rowToEntry(r: Row): LibraryEntry | null {
  const v = (r.custom_block_versions ?? []).reduce<VersionRow | null>((b, c) => (!b || c.version > b.version ? c : b), null);
  if (!v) return null;
  return {
    id: r.id, source: 'user', category: r.category, blockType: r.block_type, name: r.name, description: r.description,
    tags: r.tags ?? [], version: v.version, tier: { publicVisible: true, aiSafe: r.ai_safe, optimizerSafe: r.optimizer_safe },
    code: v.code ?? '', tslAst: (v.tsl_ast as LibraryEntry['tslAst']) ?? undefined, paramSchema: (v.param_schema ?? {}) as LibraryEntry['paramSchema'], author: r.author_id, downloads: r.downloads, status: r.status,
  };
}

/** ai_safe public user blocks (joined with their latest version). RLS + the filters drop anything that is
 *  not public+published, so disabled/delisted blocks vanish immediately. */
export async function fetchAiSafeUserBlocks(): Promise<LibraryEntry[]> {
  const { data, error } = await supabase
    .from('custom_block_templates')
    .select('id, author_id, category, block_type, name, description, tags, ai_safe, optimizer_safe, status, downloads, custom_block_versions(version, code, tsl_ast, param_schema)')
    .eq('source', 'user').eq('public_visible', true).eq('status', 'published').eq('ai_safe', true)
    .limit(200);
  if (error || !data) return [];
  return (data as unknown as Row[]).map(rowToEntry).filter((e): e is LibraryEntry => !!e);
}

/** The full ai_safe catalog the generator may reference: curated ai_safe + public user ai_safe. */
export async function fetchAiSafeCatalog(): Promise<LibraryEntry[]> {
  const curated = curatedEntries().filter((e) => e.tier.aiSafe);
  const user = await fetchAiSafeUserBlocks().catch(() => [] as LibraryEntry[]);
  return [...curated, ...user];
}

/** Build the metadata-only catalog cards to send with a generate-strategy request (gated by the caller's
 *  aiCatalog kill switch). Returns [] when disabled so the request carries nothing. */
export async function buildCatalogForRequest(query: string | undefined, enabled: boolean, limit = 12): Promise<CatalogCard[]> {
  if (!enabled) return [];
  const entries = await fetchAiSafeCatalog().catch(() => [] as LibraryEntry[]);
  return searchCatalog(entries, query, limit);
}

export interface RefResolution { blocks: Block[]; resolved: number; dropped: Array<{ libraryBlockId: string; reason: string }>; }

/** Resolve AI-emitted library references in a generated strategy into real, provenance-stamped blocks.
 *  A reference is a CUSTOM block carrying `data.libraryBlockId` but no inline `code`. Each is resolved
 *  against the ai_safe catalog (code from the immutable version, params schema-validated). A reference
 *  that can't be resolved (not ai_safe / disabled / bad params / version gone) is DROPPED with a reason —
 *  never executed blind. Blocks without a library reference pass through untouched. */
export async function resolveStrategyLibraryRefs(blocks: Block[]): Promise<RefResolution> {
  const isRef = (b: Block) => b?.type === 'custom' && (b.data as { libraryBlockId?: string })?.libraryBlockId && !(b.data as { code?: string })?.code && !(b.data as { tslAst?: unknown })?.tslAst;
  if (!blocks.some(isRef)) return { blocks, resolved: 0, dropped: [] };
  const entries = await fetchAiSafeCatalog().catch(() => [] as LibraryEntry[]);
  const byId = new Map(entries.map((e) => [e.id, e]));
  const dropped: RefResolution['dropped'] = [];
  let resolved = 0;
  const out: Block[] = [];
  for (const b of blocks) {
    if (!isRef(b)) { out.push(b); continue; }
    const d = b.data as { libraryBlockId: string; libraryVersion?: number; parameters?: Record<string, unknown> };
    const entry = byId.get(d.libraryBlockId);
    const r = resolveLibraryReference({ libraryBlockId: d.libraryBlockId, libraryVersion: d.libraryVersion ?? entry?.version ?? 0, parameters: d.parameters }, entry);
    if ('error' in r) { dropped.push({ libraryBlockId: d.libraryBlockId, reason: r.error }); continue; }
    out.push({ ...r.block, id: b.id, position: b.position, connections: b.connections }); // keep the AI's graph wiring
    resolved++;
  }
  return { blocks: out, resolved, dropped };
}
